import { PropsWithChildren, ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/classes";
import { ProgressSpinner } from "../loader";
import { Pill } from "../pill";
import { PageNavSection } from "./page-nav";
import styles from "./section.module.css";

declare module "react" {
  interface CSSProperties {
    // Catchall for CSS variables
    [key: `--${string}`]: string | number;
    //   Add custom CSS var definitions
    "--page_size"?: number;
    "--visible-columns-count"?: number;
    // Missing standard properties
    anchorName?: string;
  }
}

// TODO: Update this to a router type when we have a standardised router
export type SectionId =
  | "actions"
  | "about"
  | "highlights"
  | "pcg"
  | "signals"
  | "founders"
  | "sentiment"
  | "performance"
  | "performance-next"
  | "basecamp"
  | "funding"
  | "news"
  | "attachments"
  | "comments"
  | "network-signals"
  | "network"
  | "key-updates"
  | "entity-files";

type SectionProps = {
  id: SectionId;
  className?: string;
  onViewLeave: (sectionName: string) => void;
  onViewEnter: (sectionName: string) => void;

  onSectionRegister: (sectionName: string) => void;
  onSectionUnregister: (sectionName: string) => void;

  maximised?: boolean;
};

export const PageSection = ({
  id,
  children,
  className,
  onSectionRegister,
  onSectionUnregister,
  onViewEnter,
  onViewLeave,
  maximised,
}: PropsWithChildren<SectionProps>) => {
  const content = (
    <div
      className={cn(
        "rounded-sm border border-gray-300",
        styles.section,
        className,
        { [styles.maximised]: maximised },
      )}
    >
      <PageNavSection
        id={id}
        className={cn("flex flex-col")}
        anchorClassName={"-top-32"}
        onSectionRegister={onSectionRegister}
        onSectionUnregister={onSectionUnregister}
        onViewEnter={onViewEnter}
        onViewLeave={onViewLeave}
        viewportOptions={{
          margin: "-110px 0px 0px 0px", // top marginbecause we have two headers
          amount: 0.5,
        }}
      >
        {children}
      </PageNavSection>
    </div>
  );
  return (
    <>
      {maximised &&
        createPortal(
          <div
            className={styles.backdrop}
            style={{ ["--section-anchor"]: `--${id}` }}
          >
            <div
              className={styles.backdropContainer}
              style={
                {
                  // positionAnchor: `--${id}`,
                  // top: `anchor(--${id} top)`,
                  // left: `anchor(--${id} left)`,
                  // bottom: `anchor(--${id} bottom)`,
                  // right: `anchor(--${id} right)`,
                  // minWidth: `anchor-size(--${id} width)`,
                  // minHeight: `anchor-size(--${id} height)`,
                }
              }
            >
              {content}
            </div>
          </div>,
          document.body,
        )}
      <div style={{ anchorName: `--${id}` }}>{content}</div>
    </>
  );
};

type CounterValue = number | "loading" | "error" | undefined;

export type SectionHeaderProps = {
  title: string;
  counter?: CounterValue;
  counterClassName?: string;
  actions?: ReactNode;
  className?: string;
  titleActions?: ReactNode;
};

export const SectionCounter = ({ value, className }: { value: CounterValue; className?: string }) =>
  typeof value === "number" ? (
    <div className={cn("ml-1 py-1 px-1 text-xs bg-gray-300 inline-block rounded-xxs", className)}>
      {value}
    </div>
  ) : value === "loading" ? (
    <ProgressSpinner className="ml-2 size-4 inline-block" />
  ) : value === "error" ? (
    <Pill.Error className="ml-2" />
  ) : (
    <div className="ml-2 size-4 inline-block" />
  );

export const SectionHeader = ({
  title,
  actions,
  className,
  counter,
  counterClassName,
  titleActions,
}: SectionHeaderProps) => (
  <div
    className={cn(
      "flex w-full items-center justify-between space-x-2",
      styles.header,
      className,
    )}
  >
    <h5 className="text-base font-semibold lg:text-xl flex-shrink-0 flex items-center">
      {title}
      <SectionCounter value={counter} className={counterClassName} />
    </h5>
    {titleActions}
    {actions}
  </div>
);
